import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CloseLeadDialogComponent } from '@common/close-lead-dialog/close-lead-dialog.component';
import { LeadsService } from '@services/api/leads.service';
import { ToastrService } from 'ngx-toastr';
import { SharedModule as PrimeSharedModule } from 'primeng/api';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

/**
 * "Remmi AI suggests this lead may no longer be worth pursuing" (REM-3537_2026-10-13 §4) — the lead
 * page banner from the client's mock-up. Shown only when `GET …/close-suggestion` says `suggest`,
 * with the backend's own `title` and `message`.
 *
 * - Keep as Contact Attempted → `POST …/dismiss`; the backend hides it (here and on the Action Today
 *   card) until 3 more unanswered attempts.
 * - Move to No Further Interest → the shared closing form in a popover under the button, reason
 *   defaulted to the suggested one.
 * - ✕ → hidden for this visit only.
 *
 * The host bumps `refreshKey` after each save, since a save can log the attempt that tips it over.
 */
@Component({
  selector: 'app-close-suggestion-banner',
  standalone: true,
  imports: [CommonModule, OverlayPanelModule, TooltipModule, PrimeSharedModule, CloseLeadDialogComponent],
  templateUrl: './close-suggestion-banner.component.html',
  styleUrls: ['./close-suggestion-banner.component.scss'],
})
export class CloseSuggestionBannerComponent implements OnChanges, OnDestroy {
  @Input() leadId: string = null;
  @Input() leadName: string = null;
  @Input() refreshKey: number = 0;
  /** The lead was closed from the banner — the host reloads it. */
  @Output() leadClosed = new EventEmitter<void>();

  suggestion: any = null;
  isDismissing = false;
  /** The closing form's popover is open (it renders under the "Move to No Further Interest" button). */
  isCloseFormOpen = false;
  /** ✕ on the banner — hidden for this visit only, without telling Remmi anything. */
  isHidden = false;

  private loadSub: Subscription;
  private reloadTimer: ReturnType<typeof setTimeout> = null;
  private readonly RELOAD_DEBOUNCE_MS = 2000;

  constructor(
    private leadsService: LeadsService,
    private toaster: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId']) {
      this.isHidden = false;
    }
    if (changes['leadId']) {
      this.load();
    } else if (changes['refreshKey']) {
      this.scheduleLoad();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.reloadTimer);
    this.loadSub?.unsubscribe();
  }

  get isVisible(): boolean {
    return this.suggestion?.suggest === true;
  }

  /** refreshKey bumps on every 1.5 s auto-save; only the settled state after a burst of saves matters. */
  private scheduleLoad(): void {
    clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => this.load(), this.RELOAD_DEBOUNCE_MS);
  }

  private load(): void {
    clearTimeout(this.reloadTimer);
    this.reloadTimer = null;
    this.loadSub?.unsubscribe();
    if (!this.leadId) {
      this.suggestion = null;
      return;
    }
    const leadId = this.leadId;
    this.loadSub = this.leadsService.getLeadCloseSuggestion(leadId).subscribe({
      next: (response: any) => {
        // A late answer for the lead this tab showed before must not land on the next one.
        if (leadId === this.leadId) {
          this.suggestion = response?.data || null;
        }
      },
      error: () => {
        this.suggestion = null;
      },
    });
  }

  keep(): void {
    if (!this.leadId || this.isDismissing) {
      return;
    }
    this.isDismissing = true;
    this.leadsService.dismissLeadCloseSuggestion(this.leadId).subscribe({
      next: () => {
        this.isDismissing = false;
        this.suggestion = null;
      },
      error: error => {
        this.isDismissing = false;
        this.toaster.error(error?.message || 'Could not keep the lead. Please try again.');
      },
    });
  }

  onLeadClosed(): void {
    this.suggestion = null;
    this.leadClosed.emit();
  }
}
